import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import {
  FiMail, FiEdit, FiSave, FiX, FiTrash2,
  FiEye, FiCopy, FiSend, FiPlus, FiSearch,
  FiRefreshCw, FiCheckCircle, FiAlertCircle, FiTag,
  FiDownload, FiUpload, FiPower, FiFilter,
  FiLayers, FiMoreVertical, FiMaximize2,
  FiSmartphone, FiMonitor, FiCode, FiCheckSquare,
  FiSquare, FiSettings, FiInfo, FiZap,
  FiClock, FiBarChart2, FiTrendingUp, FiTrendingDown,
  FiFileText, FiImage, FiLink, FiType, FiAlignLeft,
  FiBold, FiItalic, FiUnderline, FiList, FiMinimize2,
  FiHistory, FiStar, FiBookmark, FiShare2, FiShield,
  FiGlobe, FiAward, FiTarget, FiActivity, FiDatabase,
  FiWifi, FiWifiOff, FiCheck, FiAlertTriangle, FiMinus,
  FiChevronDown, FiChevronUp, FiGrid, FiColumns,
  FiCalendar, FiUsers, FiMail as FiEmailSent,
  FiArchive, FiFolder, FiFolderPlus, FiTag as FiLabel,
  FiHash, FiSliders, FiZoomIn, FiZoomOut, FiMaximize,
  FiMinimize, FiCornerUpRight, FiCornerDownRight,
  FiPlay, FiPause, FiSkipForward, FiSkipBack
} from 'react-icons/fi';
import {
  getEditableBodyText,
  prepareTemplatePayload,
  buildPreviewHtml,
  PREVIEW_VARIABLE_SAMPLES,
} from '../../utils/emailTemplateBody';

const CATEGORIES = [
  'Onboarding',
  'Sécurité',
  'Transactions',
  'Notifications',
  'Marketing',
  'Support'
];

const VARIABLE_EXAMPLES = {
  '{userName}': 'Nom complet (ex. Jean Dupont)',
  '{firstName}': 'Prénom seul (ex. Jean) — idéal pour « Bonjour … »',
  '{prenom}': 'Prénom (alias de {firstName})',
  '{lastName}': 'Nom de famille',
  '{nom}': 'Nom de famille (alias)',
  '{loginUrl}': 'URL de connexion',
  '{resetLink}': 'Lien de réinitialisation',
  '{expirationTime}': 'Temps d\'expiration',
  '{quoteId}': 'ID du devis',
  '{amount}': 'Montant',
  '{clientName}': 'Nom du client',
  '{transactionId}': 'ID de transaction',
  '{paymentDate}': 'Date de paiement',
  '{userEmail}': 'Email de l\'utilisateur',
  '{companyName}': 'Nom de l\'entreprise',
  '{orderNumber}': 'Numéro de commande'
};

function AdminEmailTemplates() {
  // États principaux
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedTemplates, setSelectedTemplates] = useState(new Set());
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testVariables, setTestVariables] = useState({});
  const [sendingTest, setSendingTest] = useState(false);
  const [showVariablesPanel, setShowVariablesPanel] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [htmlValidation, setHtmlValidation] = useState({ valid: true, errors: [] });
  
  // Nouvelles fonctionnalités - Modals
  const [showSMTPModal, setShowSMTPModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showSnippetsModal, setShowSnippetsModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showABTestModal, setShowABTestModal] = useState(false);
  
  // Nouvelles fonctionnalités - États
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [checkingSMTP, setCheckingSMTP] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid, list, compact
  const [sortBy, setSortBy] = useState('updated'); // updated, name, category, created
  const [sortOrder, setSortOrder] = useState('desc');
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [editorTheme, setEditorTheme] = useState('dark');
  const [showCodeEditor, setShowCodeEditor] = useState(true);
  const [htmlMinified, setHtmlMinified] = useState(false);
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [templateTags, setTemplateTags] = useState([]);
  const [filterTags, setFilterTags] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [templateToCompare, setTemplateToCompare] = useState(null);
  const [searchInCode, setSearchInCode] = useState('');
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showLineNumbers, setShowLineNumbers] = useState(true);
  const [wordWrap, setWordWrap] = useState(true);
  const [favorites, setFavorites] = useState(new Set());
  const [recentTemplates, setRecentTemplates] = useState([]);
  const [templateStats, setTemplateStats] = useState({});
  const [emailLogs, setEmailLogs] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [templateVersions, setTemplateVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [snippets, setSnippets] = useState([
    { id: 1, name: 'Salutation', content: 'Bonjour {userName},', category: 'layout' },
    { id: 2, name: 'Signature', content: "Cordialement,\nL'équipe Liberty", category: 'layout' },
    { id: 3, name: 'Lien connexion', content: 'Connectez-vous ici : {loginUrl}', category: 'button' },
  ]);
  const [abTestVariants, setAbTestVariants] = useState([]);
  
  // Refs
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const autoSaveTimerRef = useRef(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    filterTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, searchTerm, filterCategory, filterStatus]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAllEmailTemplates();
      if (response && response.success) {
        setTemplates(response.data || []);
      } else {
        throw new Error('Erreur lors du chargement des templates');
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des templates. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    // Filtrer par recherche
    if (searchTerm) {
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (template.description && template.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtrer par catégorie
    if (filterCategory !== 'all') {
      filtered = filtered.filter(template => template.category === filterCategory);
    }

    // Filtrer par statut
    if (filterStatus === 'active') {
      filtered = filtered.filter(template => template.isActive);
    } else if (filterStatus === 'inactive') {
      filtered = filtered.filter(template => !template.isActive);
    }

    setFilteredTemplates(filtered);
  };

  const handleEditTemplate = (template) => {
    setEditForm({
      ...template,
      bodyText: getEditableBodyText(template),
    });
    setSelectedTemplate(template);
    setPreviewMode(false);
    setShowCreateModal(false);
    setHtmlValidation({ valid: true, errors: [] });
  };

  const handleCreateTemplate = () => {
    setEditForm({
      name: '',
      subject: '',
      bodyHtml: '',
      bodyText: '',
      category: 'Notifications',
      variables: [],
      description: '',
      isActive: true
    });
    setSelectedTemplate(null);
    setPreviewMode(false);
    setShowCreateModal(true);
    setHtmlValidation({ valid: true, errors: [] });
  };

  const handleSaveTemplate = async () => {
    const bodyText = String(editForm.bodyText ?? '').trim();
    if (!editForm.name || !editForm.subject || !bodyText || !editForm.category) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const payload = prepareTemplatePayload({ ...editForm, bodyText });

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      if (selectedTemplate) {
        const response = await api.updateEmailTemplate(selectedTemplate.id, payload);
        if (response && response.success) {
          setSuccessMessage('Template mis à jour avec succès');
          loadTemplates();
          setTimeout(() => {
            setSelectedTemplate(null);
            setEditForm(null);
            setShowCreateModal(false);
            setSuccessMessage(null);
          }, 1500);
        } else {
          throw new Error('Erreur lors de la mise à jour');
        }
      } else {
        const response = await api.createEmailTemplate(payload);
        if (response && response.success) {
          setSuccessMessage('Template créé avec succès');
          loadTemplates();
          setTimeout(() => {
            setShowCreateModal(false);
            setEditForm(null);
            setSuccessMessage(null);
          }, 1500);
        } else {
          throw new Error('Erreur lors de la création');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde du template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le template "${template.name}" ?`)) {
      return;
    }

    try {
      setError(null);
      const response = await api.deleteEmailTemplate(template.id);
      if (response && response.success) {
        setSuccessMessage('Template supprimé avec succès');
        loadTemplates();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError('Erreur lors de la suppression du template');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      setError(null);
      const response = await api.duplicateEmailTemplate(template.id);
      if (response && response.success) {
        setSuccessMessage('Template dupliqué avec succès');
        loadTemplates();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error('Erreur lors de la duplication');
      }
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      setError('Erreur lors de la duplication du template');
    }
  };

  const handleToggleStatus = async (template) => {
    try {
      setError(null);
      const response = await api.toggleEmailTemplateStatus(template.id);
      if (response && response.success) {
        setSuccessMessage(`Template ${response.data.isActive ? 'activé' : 'désactivé'} avec succès`);
        loadTemplates();
        setTimeout(() => setSuccessMessage(null), 2000);
      } else {
        throw new Error('Erreur lors du changement de statut');
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      setError('Erreur lors du changement de statut');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    try {
      setSendingTest(true);
      setError(null);
      const customVars = Object.fromEntries(
        Object.entries(testVariables).filter(([, v]) => v != null && String(v).trim() !== '')
      );
      const response = await api.sendTestEmail(selectedTemplate.id, testEmail, customVars);
      if (response && response.success) {
        setSuccessMessage('Email de test envoyé avec succès !');
        setShowTestModal(false);
        setTestEmail('');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      setError('Erreur lors de l\'envoi de l\'email de test');
    } finally {
      setSendingTest(false);
    }
  };

  const handlePreview = (templateOrForm) => {
    const plain = templateOrForm?.bodyText ?? getEditableBodyText(templateOrForm);
    return buildPreviewHtml(plain, testVariables);
  };

  const extractVariables = (text) => {
    const variableRegex = /\{([^}]+)\}/g;
    const matches = [];
    let match;
    while ((match = variableRegex.exec(text)) !== null) {
      matches.push(match[0]);
    }
    return [...new Set(matches)];
  };

  const handleBodyChange = (value) => {
    const variables = extractVariables(`${editForm?.subject || ''}\n${value}`);
    setEditForm({ ...editForm, bodyText: value, variables });
    setHtmlValidation({
      valid: Boolean(String(value).trim()),
      errors: String(value).trim() ? [] : ['Le message est vide'],
    });
  };

  const insertVariable = (variable) => {
    if (editorRef.current) {
      const textarea = editorRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editForm.bodyText || '';
      const before = text.substring(0, start);
      const after = text.substring(end);
      const newText = before + variable + after;
      
      handleBodyChange(newText);
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(templates, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `email-templates-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    setSuccessMessage('Templates exportés avec succès');
    setTimeout(() => setSuccessMessage(null), 2000);
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        if (Array.isArray(imported)) {
          setSuccessMessage(`${imported.length} templates prêts à être importés`);
          setShowImportModal(false);
          // Note: Pour un vrai import, il faudrait créer les templates un par un via l'API
          // Pour l'instant, on affiche juste un message
        } else {
          throw new Error('Format invalide');
        }
      } catch (error) {
        setError('Erreur lors de l\'import: format de fichier invalide');
      }
    };
    reader.readAsText(file);
  };

  const handleBulkAction = async (action) => {
    if (selectedTemplates.size === 0) {
      setError('Veuillez sélectionner au moins un template');
      return;
    }

    try {
      setError(null);
      const promises = Array.from(selectedTemplates).map(id => {
        const template = templates.find(t => t.id === id);
        if (!template) return Promise.resolve();

        switch (action) {
          case 'activate':
            return template.isActive ? Promise.resolve() : api.toggleEmailTemplateStatus(id);
          case 'deactivate':
            return !template.isActive ? Promise.resolve() : api.toggleEmailTemplateStatus(id);
          case 'delete':
            return api.deleteEmailTemplate(id);
          default:
            return Promise.resolve();
        }
      });

      await Promise.all(promises);
      setSuccessMessage(`${selectedTemplates.size} template(s) mis à jour avec succès`);
      setSelectedTemplates(new Set());
      loadTemplates();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Erreur lors de l\'action en masse:', error);
      setError('Erreur lors de l\'action en masse');
    }
  };

  const toggleTemplateSelection = (id) => {
    const newSelected = new Set(selectedTemplates);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedTemplates(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedTemplates.size === filteredTemplates.length) {
      setSelectedTemplates(new Set());
    } else {
      setSelectedTemplates(new Set(filteredTemplates.map(t => t.id)));
    }
  };

  // Nouvelles fonctions - Vérification SMTP
  const checkSMTPConfig = async () => {
    try {
      setCheckingSMTP(true);
      const response = await api.checkSmtpConfig();
      if (response && response.success) {
        setSmtpStatus({
          configured: response.isConfigured,
          message: response.message || (response.isConfigured ? 'SMTP configuré correctement' : 'SMTP non configuré')
        });
        setShowSMTPModal(true);
      }
    } catch (error) {
      setError('Erreur lors de la vérification SMTP');
    } finally {
      setCheckingSMTP(false);
    }
  };

  // Auto-save
  useEffect(() => {
    if (autoSave && editForm) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      autoSaveTimerRef.current = setTimeout(() => {
        // Sauvegarde automatique silencieuse
        if (selectedTemplate && editForm.name && editForm.subject && editForm.bodyText?.trim()) {
          api.updateEmailTemplate(selectedTemplate.id, prepareTemplatePayload(editForm)).then(() => {
            setLastSaved(new Date());
          }).catch(() => {
            // Erreur silencieuse pour l'auto-save
          });
        }
      }, 30000); // Auto-save toutes les 30 secondes
    }
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [editForm, autoSave, selectedTemplate]);

  // Toggle favorite
  const toggleFavorite = (templateId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(templateId)) {
      newFavorites.delete(templateId);
    } else {
      newFavorites.add(templateId);
    }
    setFavorites(newFavorites);
    localStorage.setItem('emailTemplatesFavorites', JSON.stringify(Array.from(newFavorites)));
  };

  // Charger les favoris depuis localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('emailTemplatesFavorites');
    if (savedFavorites) {
      setFavorites(new Set(JSON.parse(savedFavorites)));
    }
  }, []);

  // Minifier HTML
  const minifyHTML = (html) => {
    return html
      .replace(/\s+/g, ' ')
      .replace(/>\s+</g, '><')
      .trim();
  };

  // Formater HTML
  const formatHTML = (html) => {
    let formatted = '';
    let indent = 0;
    const tab = '  ';
    html.split(/>\s*</).forEach((node) => {
      if (node.match(/^\/\w/)) indent--;
      formatted += tab.repeat(Math.max(0, indent)) + '<' + node + '>\r\n';
      if (node.match(/^<?\w[^>]*[^\/]$/) && !node.startsWith('input') && !node.startsWith('img')) indent++;
    });
    return formatted.substring(1, formatted.length - 3);
  };

  // Insérer snippet
  const insertSnippet = (snippet) => {
    if (editorRef.current && editForm) {
      const textarea = editorRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editForm.bodyText || '';
      const before = text.substring(0, start);
      const after = text.substring(end);
      const newText = before + snippet.content + after;
      handleBodyChange(newText);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + snippet.content.length, start + snippet.content.length);
      }, 0);
    }
  };

  // Comparer templates
  const compareTemplates = (template1, template2) => {
    setSelectedTemplate(template1);
    setTemplateToCompare(template2);
    setShowCompareModal(true);
  };

  // Trier templates
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'category':
        comparison = a.category.localeCompare(b.category);
        break;
      case 'created':
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
        break;
      case 'updated':
      default:
        comparison = new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt);
        break;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Recherche dans le code HTML
  const searchInHTML = (html, searchTerm) => {
    if (!searchTerm) return html;
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return html.replace(regex, '<mark class="bg-yellow-400 text-black">$1</mark>');
  };

  // Charger les statistiques d'un template
  const getTemplateStats = async (templateId) => {
    try {
      const response = await api.getEmailTemplateStats(templateId);
      if (response && response.success) {
        return response.data;
      }
      return {
        sent: 0,
        opened: 0,
        clicked: 0,
        bounced: 0,
        failed: 0,
        total: 0,
        openRate: 0,
        clickRate: 0,
        bounceRate: 0
      };
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      return {
        sent: 0,
        opened: 0,
        clicked: 0,
        bounced: 0,
        failed: 0,
        total: 0,
        openRate: 0,
        clickRate: 0,
        bounceRate: 0
      };
    }
  };

  // Charger les logs d'emails
  const loadEmailLogs = async (templateId) => {
    try {
      setLoading(true);
      const response = await api.getEmailTemplateLogs(templateId, 100, 0);
      if (response && response.success) {
        setEmailLogs(response.data || []);
        setShowLogsModal(true);
      } else {
        throw new Error('Erreur lors du chargement des logs');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des logs:', error);
      setError('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  };

  // Charger l'historique des versions
  const loadTemplateHistory = async (templateId) => {
    try {
      setLoading(true);
      const response = await api.getEmailTemplateHistory(templateId);
      if (response && response.success) {
        setTemplateVersions(response.data || []);
        setShowHistoryModal(true);
      } else {
        throw new Error('Erreur lors du chargement de l\'historique');
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      setError('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  // Charger les statistiques de tous les templates
  const loadAllTemplatesStats = async () => {
    try {
      const response = await api.getAllEmailTemplatesStats();
      if (response && response.success) {
        const statsMap = {};
        response.data.forEach(stat => {
          statsMap[stat.id] = stat;
        });
        setTemplateStats(statsMap);
        return statsMap;
      }
      return {};
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      return {};
    }
  };

  const templatesByCategory = sortedTemplates.reduce((acc, template) => {
    if (!acc[template.category]) acc[template.category] = [];
    acc[template.category].push(template);
    return acc;
  }, {});

  const stats = {
    total: templates.length,
    active: templates.filter(t => t.isActive).length,
    inactive: templates.filter(t => !t.isActive).length,
    byCategory: CATEGORIES.reduce((acc, cat) => {
      acc[cat] = templates.filter(t => t.category === cat).length;
      return acc;
    }, {}),
    favorites: favorites.size,
    recent: recentTemplates.length
  };

  return (
    <div className="space-y-6 p-6 bg-gray-900 min-h-screen text-gray-100">
      {/* Header */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 pb-4 border-b border-yellow-600/30">
        <div>
          <h1 className="text-4xl font-extrabold text-yellow-400 mb-2">Templates d'Emails</h1>
          <p className="text-gray-400">Gérez les templates d'emails envoyés aux utilisateurs</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 text-sm font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all border border-gray-700"
            title="Exporter tous les templates"
          >
            <FiDownload className="w-4 h-4 mr-2" />
            Exporter
          </button>
          <label className="flex items-center px-4 py-2 text-sm font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all border border-gray-700 cursor-pointer">
            <FiUpload className="w-4 h-4 mr-2" />
            Importer
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImport}
              accept=".json"
              className="hidden"
            />
          </label>
          <button
            onClick={loadTemplates}
            disabled={loading}
            className="flex items-center px-4 py-2 text-sm font-semibold text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all border border-gray-700 disabled:opacity-50"
          >
            <FiRefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
          <button
            onClick={handleCreateTemplate}
            className="flex items-center px-5 py-2.5 text-sm font-semibold text-gray-900 bg-yellow-600 hover:bg-yellow-500 rounded-lg transition-all duration-300 border border-yellow-600/50 hover:scale-105"
          >
            <FiPlus className="w-5 h-5 mr-2" />
            Nouveau template
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <FiAlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <FiCheckCircle className="w-5 h-5 mr-2" />
            {successMessage}
          </div>
          <button onClick={() => setSuccessMessage(null)} className="text-green-400 hover:text-green-300">
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Statistiques enrichies */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 uppercase">Total</h3>
            <FiMail className="w-5 h-5 text-yellow-400" />
          </div>
          <p className="text-4xl font-bold text-yellow-400">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-green-600/30 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 uppercase">Actifs</h3>
            <FiPower className="w-5 h-5 text-green-400" />
          </div>
          <p className="text-4xl font-bold text-green-400">{stats.active}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-600/30 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 uppercase">Inactifs</h3>
            <FiPower className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-4xl font-bold text-gray-400">{stats.inactive}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-blue-600/30 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 uppercase">Catégories</h3>
            <FiLayers className="w-5 h-5 text-blue-400" />
          </div>
          <p className="text-4xl font-bold text-blue-400">{CATEGORIES.length}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-purple-600/30 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 uppercase">Favoris</h3>
            <FiStar className="w-5 h-5 text-purple-400" />
          </div>
          <p className="text-4xl font-bold text-purple-400">{stats.favorites}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-orange-600/30 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer" onClick={() => setShowAnalyticsModal(true)}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-300 uppercase">Récents</h3>
            <FiClock className="w-5 h-5 text-orange-400" />
          </div>
          <p className="text-4xl font-bold text-orange-400">{stats.recent}</p>
        </div>
      </div>

      {/* Actions en masse */}
      {selectedTemplates.size > 0 && (
        <div className="bg-gradient-to-r from-yellow-600/20 to-yellow-500/10 border border-yellow-600/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-yellow-400 font-semibold">
                {selectedTemplates.size} template(s) sélectionné(s)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkAction('activate')}
                className="px-3 py-1.5 text-sm font-semibold text-green-400 bg-green-600/20 hover:bg-green-600/30 rounded-lg border border-green-600/30"
              >
                Activer
              </button>
              <button
                onClick={() => handleBulkAction('deactivate')}
                className="px-3 py-1.5 text-sm font-semibold text-gray-400 bg-gray-700/50 hover:bg-gray-700 rounded-lg border border-gray-600/30"
              >
                Désactiver
              </button>
              <button
                onClick={() => {
                  if (window.confirm(`Supprimer ${selectedTemplates.size} template(s) ?`)) {
                    handleBulkAction('delete');
                  }
                }}
                className="px-3 py-1.5 text-sm font-semibold text-red-400 bg-red-600/20 hover:bg-red-600/30 rounded-lg border border-red-600/30"
              >
                Supprimer
              </button>
              <button
                onClick={() => setSelectedTemplates(new Set())}
                className="px-3 py-1.5 text-sm font-semibold text-gray-400 hover:text-white"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filtres avancés enrichis */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Filtres et recherche</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center px-3 py-1.5 text-sm text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              <FiSliders className="w-4 h-4 mr-1" />
              Avancé
            </button>
            <div className="flex items-center gap-1 bg-gray-900 rounded-lg border border-gray-700 p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-yellow-600/20 text-yellow-400' : 'text-gray-400 hover:text-white'}`}
                title="Vue grille"
              >
                <FiGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-yellow-600/20 text-yellow-400' : 'text-gray-400 hover:text-white'}`}
                title="Vue liste"
              >
                <FiList className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('compact')}
                className={`p-1.5 rounded ${viewMode === 'compact' ? 'bg-yellow-600/20 text-yellow-400' : 'text-gray-400 hover:text-white'}`}
                title="Vue compacte"
              >
                <FiColumns className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un template..."
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">Toutes les catégories</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <select
            className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="active">Actifs uniquement</option>
            <option value="inactive">Inactifs uniquement</option>
          </select>
          <select
            className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="updated">Dernière modification</option>
            <option value="name">Nom</option>
            <option value="category">Catégorie</option>
            <option value="created">Date de création</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="flex items-center justify-center px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white hover:bg-gray-800 transition-colors"
            title={`Trier ${sortOrder === 'asc' ? 'croissant' : 'décroissant'}`}
          >
            {sortOrder === 'asc' ? <FiTrendingUp className="w-4 h-4" /> : <FiTrendingDown className="w-4 h-4" />}
          </button>
        </div>
        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher dans le contenu..."
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                value={searchInCode}
                onChange={(e) => setSearchInCode(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="filterFavorites"
                checked={filterTags.includes('favorites')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFilterTags([...filterTags, 'favorites']);
                  } else {
                    setFilterTags(filterTags.filter(t => t !== 'favorites'));
                  }
                }}
                className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded"
              />
              <label htmlFor="filterFavorites" className="text-sm text-gray-300 cursor-pointer">Favoris uniquement</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="filterRecent"
                checked={filterTags.includes('recent')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFilterTags([...filterTags, 'recent']);
                  } else {
                    setFilterTags(filterTags.filter(t => t !== 'recent'));
                  }
                }}
                className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded"
              />
              <label htmlFor="filterRecent" className="text-sm text-gray-300 cursor-pointer">Récents uniquement</label>
            </div>
          </div>
        )}
      </div>

      {/* Liste des templates */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500"></div>
          <p className="ml-4 text-xl text-yellow-400">Chargement des templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
          <FiMail className="w-20 h-20 text-gray-600 mx-auto mb-4" />
          <p className="text-xl text-gray-400">Aucun template trouvé</p>
          <p className="text-gray-500 mt-2">Créez votre premier template ou ajustez vos filtres</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(templatesByCategory).map(([category, categoryTemplates]) => (
            <div key={category}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <FiTag className="w-5 h-5 text-yellow-400 mr-2" />
                  <h2 className="text-2xl font-bold text-white">{category}</h2>
                  <span className="ml-3 px-3 py-1 bg-yellow-600/20 text-yellow-400 text-sm font-semibold rounded-full">
                    {categoryTemplates.length}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryTemplates.map((template) => (
                  <div
                    key={template.id}
                    className={`bg-gradient-to-br from-gray-800 to-gray-900 border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                      template.isActive 
                        ? 'border-green-600/30 hover:border-green-500/50' 
                        : 'border-gray-700/50 hover:border-gray-600/50 opacity-75'
                    } ${selectedTemplates.has(template.id) ? 'ring-2 ring-yellow-500' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedTemplates.has(template.id)}
                          onChange={() => toggleTemplateSelection(template.id)}
                          className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                        />
                        <div className={`p-2 rounded-lg ${template.isActive ? 'bg-green-600/20' : 'bg-gray-700/50'}`}>
                          <FiMail className={`w-6 h-6 ${template.isActive ? 'text-green-400' : 'text-gray-500'}`} />
                        </div>
                        {!template.isActive && (
                          <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs font-semibold rounded">
                            Inactif
                          </span>
                        )}
                      </div>
                      <div className="relative group">
                        <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
                          <FiMoreVertical className="w-4 h-4" />
                        </button>
                        <div className="absolute right-0 top-full mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-2 min-w-[180px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <button
                            onClick={() => handleEditTemplate(template)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-yellow-400 flex items-center"
                          >
                            <FiEdit className="w-4 h-4 mr-2" /> Éditer
                          </button>
                          <button
                            onClick={() => {
                              setSelectedTemplate(template);
                              setTestVariables({ ...PREVIEW_VARIABLE_SAMPLES });
                              setShowTestModal(true);
                              setTestEmail('');
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-blue-400 flex items-center"
                          >
                            <FiSend className="w-4 h-4 mr-2" /> Test Email
                          </button>
                          <button
                            onClick={() => handleDuplicateTemplate(template)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-purple-400 flex items-center"
                          >
                            <FiCopy className="w-4 h-4 mr-2" /> Dupliquer
                          </button>
                          <button
                            onClick={() => handleToggleStatus(template)}
                            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-green-400 flex items-center"
                          >
                            <FiPower className="w-4 h-4 mr-2" /> {template.isActive ? 'Désactiver' : 'Activer'}
                          </button>
                          <div className="border-t border-gray-700 my-1"></div>
                          <button
                            onClick={() => handleDeleteTemplate(template)}
                            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700 flex items-center"
                          >
                            <FiTrash2 className="w-4 h-4 mr-2" /> Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{template.name}</h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">{template.subject}</p>
                    {template.description && (
                      <p className="text-gray-500 text-xs mb-4 line-clamp-2">{template.description}</p>
                    )}
                    {template.variables && template.variables.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {template.variables.slice(0, 3).map((variable, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded border border-yellow-600/30"
                            title={VARIABLE_EXAMPLES[variable] || ''}
                          >
                            {variable}
                          </span>
                        ))}
                        {template.variables.length > 3 && (
                          <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded">
                            +{template.variables.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                      <button
                        onClick={() => setSelectedTemplate(template)}
                        className="flex items-center text-sm text-blue-400 hover:text-blue-300"
                      >
                        <FiEye className="w-4 h-4 mr-1" />
                        Aperçu
                      </button>
                      <p className="text-xs text-gray-500">
                        {template.updatedAt ? new Date(template.updatedAt).toLocaleDateString('fr-FR') : 'N/A'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal d'édition/création */}
      {(editForm || showCreateModal) && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-yellow-400">
                {selectedTemplate ? 'Éditer le template' : 'Nouveau template'}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowVariablesPanel(!showVariablesPanel)}
                  className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Afficher les variables"
                >
                  <FiZap className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setSelectedTemplate(null);
                    setEditForm(null);
                    setShowCreateModal(false);
                    setError(null);
                  }}
                  className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-hidden flex gap-4">
              {/* Panneau de variables */}
              {showVariablesPanel && (
                <div className="w-64 bg-gray-900/50 border border-gray-700 rounded-lg p-4 overflow-y-auto">
                  <h3 className="text-sm font-semibold text-white mb-3">Variables disponibles</h3>
                  <div className="space-y-2">
                    {Object.entries(VARIABLE_EXAMPLES).map(([variable, description]) => (
                      <button
                        key={variable}
                        onClick={() => insertVariable(variable)}
                        className="w-full text-left p-2 bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 hover:border-yellow-600/50 transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <code className="text-xs text-yellow-400 font-mono">{variable}</code>
                          <FiPlus className="w-3 h-3 text-gray-500 group-hover:text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Contenu principal */}
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Nom du template <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={editForm?.name || ''}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                      placeholder="Ex: Email de bienvenue"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Catégorie <span className="text-red-400">*</span>
                    </label>
                    <select
                      value={editForm?.category || ''}
                      onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Sujet <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={editForm?.subject || ''}
                    onChange={(e) => setEditForm({...editForm, subject: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    placeholder="Ex: Bienvenue sur Liberty !"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editForm?.description || ''}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    rows="2"
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    placeholder="Description du template..."
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Corps de l'email <span className="text-red-400">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPreviewMode(!previewMode)}
                        className="text-sm text-yellow-400 hover:text-yellow-300 flex items-center px-3 py-1 bg-yellow-600/20 rounded-lg border border-yellow-600/30"
                      >
                        <FiEye className="w-4 h-4 mr-1" />
                        {previewMode ? 'Éditer' : 'Aperçu'}
                      </button>
                      {previewMode && (
                        <div className="flex items-center gap-1 bg-gray-900 rounded-lg border border-gray-700 p-1">
                          <button
                            onClick={() => setPreviewDevice('desktop')}
                            className={`p-1.5 rounded ${previewDevice === 'desktop' ? 'bg-yellow-600/20 text-yellow-400' : 'text-gray-400 hover:text-white'}`}
                            title="Desktop"
                          >
                            <FiMonitor className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setPreviewDevice('mobile')}
                            className={`p-1.5 rounded ${previewDevice === 'mobile' ? 'bg-yellow-600/20 text-yellow-400' : 'text-gray-400 hover:text-white'}`}
                            title="Mobile"
                          >
                            <FiSmartphone className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {htmlValidation.errors.length > 0 && (
                    <div className="mb-2 p-2 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-xs">
                      {htmlValidation.errors.map((err, i) => (
                        <div key={i}>⚠ {err}</div>
                      ))}
                    </div>
                  )}
                  {previewMode ? (
                    <div
                      className={`w-full px-4 py-4 bg-white rounded-lg overflow-auto border border-gray-700 ${
                        previewDevice === 'mobile' ? 'max-w-sm mx-auto' : 'min-h-[400px]'
                      }`}
                      dangerouslySetInnerHTML={{ __html: handlePreview(editForm) }}
                    />
                  ) : (
                    <>
                      <textarea
                        ref={editorRef}
                        value={editForm?.bodyText || ''}
                        onChange={(e) => handleBodyChange(e.target.value)}
                        rows="16"
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 text-sm leading-relaxed"
                        placeholder={'Bonjour {userName},\n\nVotre message ici...\n\nCordialement,\nL\'équipe Liberty'}
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Texte simple : les retours à la ligne sont conservés. Utilisez les variables comme {'{userName}'} — le HTML est généré automatiquement à l&apos;envoi.
                      </p>
                    </>
                  )}
                </div>

                {editForm?.variables && editForm.variables.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Variables détectées ({editForm.variables.length})
                    </label>
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                      {editForm.variables.map((variable, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-yellow-600/20 text-yellow-400 text-sm rounded border border-yellow-600/30"
                          title={VARIABLE_EXAMPLES[variable] || ''}
                        >
                          {variable}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm?.isActive !== false}
                      onChange={(e) => setEditForm({...editForm, isActive: e.target.checked})}
                      className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                    />
                    <span className="text-sm font-medium text-gray-300">Template actif</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex space-x-4 pt-6 mt-6 border-t border-gray-700">
              <button
                onClick={handleSaveTemplate}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiSave className="w-5 h-5 mr-2" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                onClick={() => {
                  setSelectedTemplate(null);
                  setEditForm(null);
                  setShowCreateModal(false);
                  setError(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de test email */}
      {showTestModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Envoyer un email de test</h2>
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestEmail('');
                  setTestVariables({});
                }}
                className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email de destination <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  placeholder="test@example.com"
                />
              </div>

              {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Variables personnalisées (optionnel)
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                    {selectedTemplate.variables.map((variable, index) => (
                      <div key={index}>
                        <label className="block text-xs text-gray-400 mb-1">{variable}</label>
                        <input
                          type="text"
                          value={testVariables[variable] || ''}
                          onChange={(e) => setTestVariables({...testVariables, [variable]: e.target.value})}
                          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-yellow-500"
                          placeholder={VARIABLE_EXAMPLES[variable] || 'Valeur...'}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex space-x-4 pt-4">
                <button
                  onClick={handleSendTestEmail}
                  disabled={sendingTest || !testEmail}
                  className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiSend className="w-5 h-5 mr-2" />
                  {sendingTest ? 'Envoi...' : 'Envoyer'}
                </button>
                <button
                  onClick={() => {
                    setShowTestModal(false);
                    setTestEmail('');
                    setTestVariables({});
                  }}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de prévisualisation */}
      {selectedTemplate && !editForm && !showTestModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{selectedTemplate.name}</h2>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Sujet</label>
                <p className="text-white bg-gray-900 px-4 py-2 rounded-lg">{selectedTemplate.subject}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Aperçu</label>
                <div
                  className="w-full px-4 py-4 bg-white rounded-lg min-h-[400px] overflow-auto border border-gray-700"
                  dangerouslySetInnerHTML={{ __html: handlePreview(selectedTemplate) }}
                />
              </div>
              {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Variables disponibles</label>
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-900/50 rounded-lg border border-gray-700">
                    {selectedTemplate.variables.map((variable, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-yellow-600/20 text-yellow-400 text-sm rounded border border-yellow-600/30"
                        title={VARIABLE_EXAMPLES[variable] || ''}
                      >
                        {variable}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-4 pt-6 mt-6 border-t border-gray-700">
              <button
                onClick={() => handleEditTemplate(selectedTemplate)}
                className="flex-1 px-4 py-3 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-colors flex items-center justify-center"
              >
                <FiEdit className="w-5 h-5 mr-2" />
                Éditer
              </button>
              <button
                onClick={() => {
                  setTestVariables({ ...PREVIEW_VARIABLE_SAMPLES });
                  setShowTestModal(true);
                  setTestEmail('');
                }}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg transition-colors flex items-center justify-center"
              >
                <FiSend className="w-5 h-5 mr-2" />
                Test Email
              </button>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de vérification SMTP */}
      {showSMTPModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Configuration SMTP</h2>
              <button onClick={() => setShowSMTPModal(false)} className="text-gray-400 hover:text-white">
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${smtpStatus?.configured ? 'bg-green-500/20 border border-green-500/50' : 'bg-red-500/20 border border-red-500/50'}`}>
                <div className="flex items-center gap-3">
                  {smtpStatus?.configured ? (
                    <FiCheckCircle className="w-6 h-6 text-green-400" />
                  ) : (
                    <FiAlertCircle className="w-6 h-6 text-red-400" />
                  )}
                  <div>
                    <p className={`font-semibold ${smtpStatus?.configured ? 'text-green-400' : 'text-red-400'}`}>
                      {smtpStatus?.configured ? 'SMTP Configuré' : 'SMTP Non Configuré'}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">{smtpStatus?.message}</p>
                  </div>
                </div>
              </div>
              {!smtpStatus?.configured && (
                <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                  <p className="text-sm text-gray-300 mb-2">Pour configurer SMTP, ajoutez ces variables dans votre fichier .env :</p>
                  <code className="block text-xs text-yellow-400 bg-gray-800 p-2 rounded">
                    SMTP_HOST=smtp.gmail.com<br />
                    SMTP_PORT=587<br />
                    SMTP_USER=votre-email@gmail.com<br />
                    SMTP_PASS=votre-mot-de-passe<br />
                    SMTP_FROM="Liberty" &lt;noreply@liberty.com&gt;
                  </code>
                </div>
              )}
              <button
                onClick={() => setShowSMTPModal(false)}
                className="w-full px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de snippets */}
      {showSnippetsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Snippets de code</h2>
              <button onClick={() => setShowSnippetsModal(false)} className="text-gray-400 hover:text-white">
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-4">
              {snippets.map((snippet) => (
                <div key={snippet.id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 hover:border-yellow-600/50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white">{snippet.name}</h3>
                    <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded">{snippet.category}</span>
                  </div>
                  <code className="block text-xs text-gray-400 bg-gray-800 p-2 rounded mb-3 line-clamp-3">{snippet.content}</code>
                  <button
                    onClick={() => {
                      insertSnippet(snippet);
                      setShowSnippetsModal(false);
                    }}
                    className="w-full px-3 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-colors text-sm"
                  >
                    Insérer
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de statistiques */}
      {showAnalyticsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Statistiques des templates</h2>
              <button onClick={() => setShowAnalyticsModal(false)} className="text-gray-400 hover:text-white">
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => {
                const stats = getTemplateStats(template.id);
                const openRate = stats.sent > 0 ? ((stats.opened / stats.sent) * 100).toFixed(1) : 0;
                const clickRate = stats.sent > 0 ? ((stats.clicked / stats.sent) * 100).toFixed(1) : 0;
                return (
                  <div key={template.id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-white mb-3">{template.name}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Envoyés:</span>
                        <span className="text-white font-semibold">{stats.sent}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Ouverts:</span>
                        <span className="text-green-400 font-semibold">{stats.opened} ({openRate}%)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Clics:</span>
                        <span className="text-blue-400 font-semibold">{stats.clicked} ({clickRate}%)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Bounces:</span>
                        <span className="text-red-400 font-semibold">{stats.bounced}</span>
                      </div>
                      <button
                        onClick={() => loadEmailLogs(template.id)}
                        className="w-full mt-3 px-3 py-2 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 rounded-lg transition-colors text-sm"
                      >
                        Voir les logs
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Modal d'historique des versions */}
      {showHistoryModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Historique des versions - {selectedTemplate.name}</h2>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-white">
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3">
              {templateVersions.map((version) => (
                <div key={version.id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 hover:border-yellow-600/50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-yellow-600/20 text-yellow-400 text-sm font-semibold rounded">v{version.version}</span>
                      <span className="text-gray-400 text-sm">{new Date(version.createdAt).toLocaleString('fr-FR')}</span>
                    </div>
                    <span className="text-gray-400 text-sm">par {version.createdBy}</span>
                  </div>
                  <p className="text-gray-300 text-sm">{version.changes}</p>
                  <button
                    onClick={() => {
                      setSelectedVersion(version);
                      // Charger cette version dans l'éditeur
                    }}
                    className="mt-2 text-yellow-400 hover:text-yellow-300 text-sm"
                  >
                    Restaurer cette version
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de logs d'emails */}
      {showLogsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Logs d'envoi d'emails</h2>
              <button onClick={() => setShowLogsModal(false)} className="text-gray-400 hover:text-white">
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-900/50 border-b border-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Statut</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Ouvert</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Clic</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLogs.map((log) => (
                    <tr key={log.id} className="border-b border-gray-700 hover:bg-gray-900/50">
                      <td className="px-4 py-3 text-sm text-gray-300">{log.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          log.status === 'sent' || log.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                          log.status === 'bounced' || log.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                          log.status === 'opened' ? 'bg-blue-500/20 text-blue-400' :
                          log.status === 'clicked' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">{log.sentAt ? new Date(log.sentAt).toLocaleString('fr-FR') : 'N/A'}</td>
                      <td className="px-4 py-3">
                        {log.openedAt ? <FiCheckCircle className="w-5 h-5 text-green-400" title={new Date(log.openedAt).toLocaleString('fr-FR')} /> : <FiX className="w-5 h-5 text-gray-600" />}
                      </td>
                      <td className="px-4 py-3">
                        {log.clickedAt ? <FiCheckCircle className="w-5 h-5 text-blue-400" title={new Date(log.clickedAt).toLocaleString('fr-FR')} /> : <FiX className="w-5 h-5 text-gray-600" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal de comparaison */}
      {showCompareModal && selectedTemplate && templateToCompare && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Comparaison de templates</h2>
              <button onClick={() => setShowCompareModal(false)} className="text-gray-400 hover:text-white">
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-4">
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-3">{selectedTemplate.name}</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-gray-400">Sujet:</span> <span className="text-white">{selectedTemplate.subject}</span></div>
                  <div><span className="text-gray-400">Catégorie:</span> <span className="text-white">{selectedTemplate.category}</span></div>
                  <div className="mt-4 p-3 bg-gray-800 rounded border border-gray-700">
                    <div dangerouslySetInnerHTML={{ __html: selectedTemplate.bodyHtml?.substring(0, 500) + '...' }} />
                  </div>
                </div>
              </div>
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-3">{templateToCompare.name}</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-gray-400">Sujet:</span> <span className="text-white">{templateToCompare.subject}</span></div>
                  <div><span className="text-gray-400">Catégorie:</span> <span className="text-white">{templateToCompare.category}</span></div>
                  <div className="mt-4 p-3 bg-gray-800 rounded border border-gray-700">
                    <div dangerouslySetInnerHTML={{ __html: templateToCompare.bodyHtml?.substring(0, 500) + '...' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminEmailTemplates;
